import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";

import { toast } from "react-toastify";
import openSocket from "../../services/socket-io";
import clsx from "clsx";

import { Paper, makeStyles } from "@material-ui/core";

import ContactDrawer from "../ContactDrawer";
import MessageInput from "../MessageInput/";
import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";
import TicketActionButtons from "../TicketActionButtons";
import MessagesList from "../MessagesList";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import { Typography } from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import toastError from "../../errors/toastError";

const drawerWidth = 320;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100%",
    position: "relative",
    overflow: "hidden",
  },

  ticketInfo: {
    flex: "1 1 auto",
    minWidth: 0,
    overflow: "hidden",
    [theme.breakpoints.down("xs")]: {
      maxWidth: "85%",
      flexBasis: "85%",
    },
  },
  ticketActionButtons: {
    flex: "0 0 auto",
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    [theme.breakpoints.down("xs")]: {
      maxWidth: "100%",
      flexBasis: "100%",
      marginBottom: "5px",
      justifyContent: "flex-end",
    },
  },

  mainWrapper: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: "0",
    marginRight: -drawerWidth,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  mainWrapperShift: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },

  overlay: {
    position: "absolute",
    top: 45,
    left: 0,
    width: "100%",
    height: "calc(100% - 45px)",
    backgroundColor: "rgba(37, 118, 210, 0.05)",
    zIndex: 1000,
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    flexDirection: "column",
    pointerEvents: "none",
    paddingBottom: "120px",
  },
}));

const Ticket = () => {
  const { ticketId } = useParams();
  const history = useHistory();
  const classes = useStyles();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});
  const { user } = React.useContext(AuthContext);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchTicket = async () => {
        try {
          const { data } = await api.get("/tickets/" + ticketId);

          setContact(data.contact);
          setTicket(data);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchTicket();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [ticketId, history]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("connect", () => socket.emit("joinChatBox", ticketId));

    socket.on("ticket", (data) => {
      if (data.action === "update") {
        setTicket(data.ticket);
      }

      if (data.action === "delete") {
        toast.success("Ticket deleted sucessfully.");
        history.push("/tickets");
      }
    });

    socket.on("contact", (data) => {
      if (data.action === "update") {
        setContact((prevState) => {
          if (prevState.id === data.contact?.id) {
            return { ...prevState, ...data.contact };
          }
          return prevState;
        });
      }
    });

    socket.on("appMessage", (data) => {
      if (data.action === "create" && (data.message.mediaType === "note" || data.message.mediaType === "tag" || data.message.mediaType === "schedule_history")) {
        setTicket((prevTicket) => {
          if (prevTicket && prevTicket.id === data.message.ticketId) {
            const updatedMessages = prevTicket.messages ? [...prevTicket.messages, data.message] : [data.message];
            return { ...prevTicket, messages: updatedMessages };
          }
          return prevTicket;
        });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [ticketId, history]);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const handleAcceptTicket = async () => {
    setLoading(true);
    try {
      await api.put(`/tickets/${ticketId}`, {
        status: "open",
        userId: user?.id,
      });
      setTicket((prev) => ({ ...prev, status: "open", userId: user?.id }));
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  return (
    <div className={classes.root} id="drawer-container">
      <Paper
        variant="outlined"
        elevation={0}
        className={clsx(classes.mainWrapper, {
          [classes.mainWrapperShift]: drawerOpen,
        })}
      >
        <TicketHeader loading={loading}>
          <div className={classes.ticketInfo}>
            <TicketInfo
              contact={contact}
              ticket={ticket}
              onClick={handleDrawerOpen}
            />
          </div>
          <div className={classes.ticketActionButtons}>
            <TicketActionButtons ticket={ticket} />
          </div>
        </TicketHeader>
        <ReplyMessageProvider>
          <MessagesList
            ticketId={ticketId}
            isGroup={ticket.isGroup}
          ></MessagesList>
          {ticket.status === "pending" && (
            <div className={classes.overlay}>
              <Paper
                elevation={3}
                style={{
                  padding: "20px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  borderRadius: "15px",
                  pointerEvents: "auto",
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Este ticket aún no ha sido aceptado
                </Typography>
                <ButtonWithSpinner
                  loading={loading}
                  variant="contained"
                  color="primary"
                  onClick={handleAcceptTicket}
                  style={{ marginTop: "10px" }}
                >
                  Aceptar Ticket para Responder
                </ButtonWithSpinner>
              </Paper>
            </div>
          )}
          <MessageInput ticketStatus={ticket.status} ticket={ticket} />
        </ReplyMessageProvider>
      </Paper>
      <ContactDrawer
        open={drawerOpen}
        handleDrawerClose={handleDrawerClose}
        contact={contact}
        ticket={ticket}
        loading={loading}
      />
    </div>
  );
};

export default Ticket;
